import {
  buildFormSchemaForCarriers,
  DEPRECATED_FIELD_IDS,
  getProviderIntakeFields,
  IMO,
  PINNACLE_AVAILABLE_CARRIERS,
  type FormField,
} from "@aam/shared";
import { buildProfileSections, ProfileSectionCard } from "../ProfileSectionCards";
import { Badge } from "../ui/Badge";
import { Card, CardHeader, CardTitle } from "../ui/Card";
import { REDACTED_ENCRYPTED_VALUE } from "./types";

const CARRIER_LABELS = new Map(PINNACLE_AVAILABLE_CARRIERS.map((c) => [c.value, c.label]));

function carrierLabel(value: string) {
  return CARRIER_LABELS.get(value) ?? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fallbackLabel(key: string) {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function fieldMetadata(carriers: string[]) {
  const schema = buildFormSchemaForCarriers(carriers);
  const labels = new Map<string, string>();
  const optionLabels = new Map<string, Map<string, string>>();

  for (const section of schema.sections) {
    for (const field of section.fields) {
      labels.set(field.id, field.label);
      if (field.options?.length) {
        optionLabels.set(field.id, new Map(field.options.map((option) => [option.value, option.label])));
      }
    }
  }

  return { schema, labels, optionLabels };
}

function formatFieldValue(value: unknown, options?: Map<string, string>) {
  if (value === REDACTED_ENCRYPTED_VALUE) return "Encrypted field hidden";
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    return value.map((item) => options?.get(String(item)) ?? String(item)).join(", ");
  }
  if (typeof value === "object") {
    const file = value as { fileName?: string };
    if (file.fileName) return file.fileName;
    return JSON.stringify(value);
  }
  const text = options?.get(String(value)) ?? String(value);
  if (text.startsWith("data:image/")) return "Signature captured";
  return text.length > 240 ? `${text.slice(0, 240)}...` : text;
}

export function ProfileSectionsView({
  formData,
  carriers,
  visibleFieldIds,
  scopedCarriers,
  onCopy,
  copiedId,
  onReveal,
  revealedIds,
  revealingIds,
  revealedValues,
  allowFileDownloads = false,
  onDownloadFile,
}: {
  formData: Record<string, unknown>;
  carriers: string[];
  visibleFieldIds?: Set<string>;
  /** Provider viewers: show these carriers as badges instead of the full submission list. */
  scopedCarriers?: string[];
  onCopy?: (id: string, value: string) => void;
  copiedId?: string | null;
  onReveal?: (id: string) => void;
  revealedIds?: Set<string>;
  revealingIds?: Set<string>;
  revealedValues?: Map<string, string>;
  allowFileDownloads?: boolean;
  onDownloadFile?: (fileId: string, fileName: string) => void;
}) {
  const { schema, optionLabels } = fieldMetadata(carriers);
  const sections = buildProfileSections(schema.sections, formData, {
    visibleFieldIds,
    formatValue: (fieldId, value) => {
      if (fieldId === "selectedCarriers" && Array.isArray(value)) {
        return value.map((item) => carrierLabel(String(item))).join(", ");
      }
      return formatFieldValue(value, optionLabels.get(fieldId));
    },
    labelFor: (fieldId, field: FormField) => field.label || fallbackLabel(fieldId),
    downloadFileIdFor: allowFileDownloads
      ? (_fieldId, value) => {
          const file = value as { fileId?: string };
          return file?.fileId;
        }
      : undefined,
  }).filter((section) => {
    if (DEPRECATED_FIELD_IDS.has(section.sectionId)) return false;
    if (scopedCarriers?.length && section.sectionId === "carriers_to_contract") return false;
    return section.rows.some((row) => !DEPRECATED_FIELD_IDS.has(row.id));
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {scopedCarriers && scopedCarriers.length > 0 && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Carriers</CardTitle>
          </CardHeader>
          <div className="flex flex-wrap gap-1.5 px-4 pb-4">
            {scopedCarriers.map((carrier) => (
              <Badge key={carrier} variant="outline" className="max-w-[200px] truncate">
                {carrierLabel(carrier)}
              </Badge>
            ))}
          </div>
        </Card>
      )}
      {sections.map((section) => (
        <ProfileSectionCard
          key={section.sectionId}
          title={section.title}
          rows={section.rows.filter((row) => !DEPRECATED_FIELD_IDS.has(row.id))}
          onCopy={onCopy}
          copiedId={copiedId}
          onReveal={onReveal}
          revealedIds={revealedIds}
          revealingIds={revealingIds}
          revealedValues={revealedValues}
          onDownloadFile={onDownloadFile}
        />
      ))}
    </div>
  );
}

export function providerIntakeFieldIds(imo: IMO | undefined, carriers: string[]) {
  const schema = buildFormSchemaForCarriers(carriers);
  const allowed = new Set(getProviderIntakeFields(imo ?? IMO.PINNACLE));
  return new Set(
    schema.sections.flatMap((section) =>
      section.fields
        .filter((field) => allowed.has(field.id))
        .map((field) => field.id)
    )
  );
}
